import { useCallback, useEffect, useRef, useState } from "react";
import { getCachedUserFiles } from "../api/finster/userFilesStore";
import { isQueryableUserFileStatus } from "../api/finster/userFiles";
import { reserveUploadName } from "./uploadNames";
import type { UploadTransport } from "./uploadTransport";

async function readYourFilesForNaming(transport: UploadTransport, signal: AbortSignal) {
  while (!signal.aborted) {
    try {
      return await transport.readYourFiles(signal);
    } catch {
      if (signal.aborted) break;
      await new Promise<void>((resolve) => {
        const done = () => {
          clearTimeout(timer);
          signal.removeEventListener("abort", done);
          resolve();
        };
        const timer = setTimeout(done, 1000);
        signal.addEventListener("abort", done, { once: true });
      });
    }
  }
  return undefined;
}

export interface ComposerUpload {
  localId: string;
  name: string;
  userFileId?: string;
  ready: boolean;
}

/** One owner for reservation, overlapping batches, and status reads outside the picker. */
export function useComposerUploads(transport?: UploadTransport) {
  const [uploads, setUploads] = useState<ComposerUpload[]>([]);
  const [previousTransport, setPreviousTransport] = useState(transport);
  const entries = useRef<ComposerUpload[]>([]);
  const lifecycle = useRef(new AbortController());

  // Reset before committing children with uploads from the previous transport.
  if (transport !== previousTransport) {
    setPreviousTransport(transport);
    setUploads([]);
  }

  const publish = useCallback(() => setUploads([...entries.current]), []);

  useEffect(() => {
    const controller = new AbortController();
    lifecycle.current = controller;
    entries.current = [];
    return () => controller.abort();
  }, [transport]);

  const addUploads = useCallback(
    (files: readonly File[]) => {
      if (!transport || files.length === 0) return;
      const { signal } = lifecycle.current;
      const cachedNames = (getCachedUserFiles() ?? []).map((file) => file.name);
      const batch = files.map((file) => {
        const entry: ComposerUpload = {
          localId: crypto.randomUUID(),
          name: reserveUploadName(file.name, [
            ...cachedNames,
            ...entries.current.map((item) => item.name),
          ]),
          ready: false,
        };
        entries.current.push(entry);
        return { entry, file };
      });
      publish();

      // Reserve synchronously before awaiting the Your Files list, so overlapping batches
      // cannot claim the same name. Reconcile with a fresh full list before upload.
      void readYourFilesForNaming(transport, signal)
        .then((userFiles) => {
          if (signal.aborted || !userFiles) return;
          const batchEntries = new Set(batch.map(({ entry }) => entry));
          const reserved = [
            ...userFiles.map((item) => item.name),
            ...entries.current.filter((item) => !batchEntries.has(item)).map((item) => item.name),
          ];
          for (const { entry, file } of batch) {
            entry.name = reserveUploadName(file.name, reserved);
            reserved.push(entry.name);
          }
          publish();
          for (const { entry, file } of batch) {
            const renamed = new File([file], entry.name, {
              type: file.type,
              lastModified: file.lastModified,
            });
            void transport
              .upload(renamed, signal)
              .then((record) => {
                if (signal.aborted) return;
                entry.userFileId = record.id;
                entry.name = record.name;
                // Acceptance alone does not establish Ready; check the Your Files list.
                publish();
              })
              .catch(() => {
                // ENG-9521 owns terminal failure presentation. Keep unresolved files blocking.
              });
          }
        })
        .catch(() => {
          // Without a Your Files read we cannot safely reserve stored names. Remain pending.
        });
    },
    [publish, transport]
  );

  const hasPending = uploads.some((entry) => !entry.ready);
  const shouldPoll = uploads.some((entry) => entry.userFileId && !entry.ready);
  useEffect(() => {
    if (!transport || !shouldPoll) return undefined;
    const { signal } = lifecycle.current;
    let timer: ReturnType<typeof setTimeout>;
    let stopped = false;
    const poll = async () => {
      try {
        const userFiles = await transport.readYourFiles(signal);
        if (stopped || signal.aborted) return;
        for (const entry of entries.current) {
          if (entry.ready || !entry.userFileId) continue;
          const record = userFiles.find((item) => item.id === entry.userFileId);
          if (!record) continue;
          entry.name = record.name;
          entry.ready = isQueryableUserFileStatus(record.status);
        }
        publish();
      } catch {
        // Transient reads preserve pending state, with no timeout or cancellation UI.
      }
      if (!stopped && !signal.aborted) timer = setTimeout(() => void poll(), 1000);
    };
    timer = setTimeout(() => void poll(), 1000);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [shouldPoll, publish, transport]);

  const removeUpload = useCallback(
    (localId: string) => {
      entries.current = entries.current.filter((item) => item.localId !== localId || !item.ready);
      publish();
    },
    [publish]
  );

  return { uploads, hasPending, addUploads, removeUpload };
}
